import type { BracketMatch, BracketRound, BracketSlot } from './bracketTypes';
import type {
  ApiBracket,
  ApiCompetition,
  ApiMatch,
  ApiMatchStatus,
  ApiParticipant,
  ApiStanding,
  CompetitionSystemCode,
} from './types';
import { shortTime, toViewStatus } from './dashboardView';

/**
 * Model widokowy ekranu losowania — czyste funkcje mapujące odpowiedzi API
 * na drabinkę (`BracketRound[]`) i karty grup. Bez Reacta, łatwe do testowania.
 */

/** Rodzaj losowania wynikający z systemu rozgrywek. */
export type DrawKind = 'bracket' | 'groups' | 'round-robin' | 'none';

/**
 * Co losuje `POST /{id}/draw` dla danego systemu — drabinkę, fazę grupową
 * albo terminarz każdy z każdym. `CHALLENGE` nie ma losowania.
 */
export function drawKindOf(system: CompetitionSystemCode): DrawKind {
  switch (system) {
    case 'PLAYOFF':
      return 'bracket';
    case 'GROUP_PLAYOFF':
      return 'groups';
    case 'SINGLE_GROUP':
      return 'round-robin';
    default:
      return 'none';
  }
}

/** Najmniejsza potęga dwójki ≥ n — rozmiar drabinki dla n slotów (jak w backendzie). */
export function nextPowerOfTwo(n: number): number {
  let size = 1;
  while (size < n) size *= 2;
  return size;
}

/**
 * Etykieta rundy drabinki. Backend nazywa rundy `R1…`, `QF`, `SF`, `F` i `3P`;
 * wcześniejsze rundy nazywamy ułamkowo po liczbie meczów (8 → „1/8 finału”),
 * a jeszcze głębsze — numerem kolejki.
 */
export function bracketRoundLabel(name: string, matchesInRound: number): string {
  switch (name) {
    case 'F':
      return 'Finał';
    case 'SF':
      return 'Półfinał';
    case 'QF':
      return 'Ćwierćfinał';
    case '3P':
      return 'Mecz o 3. miejsce';
    default: {
      if (matchesInRound === 8) return '1/8 finału';
      if (matchesInRound === 16) return '1/16 finału';
      const numbered = /^R(\d+)$/.exec(name);
      return numbered ? `Runda ${numbered[1]}` : name;
    }
  }
}

/** Nazwa rundy w konwencji backendu (`getRoundNames`): ostatnie trzy to QF/SF/F. */
function backendRoundName(roundNumber: number, totalRounds: number): string {
  const fromEnd = totalRounds - roundNumber;
  if (fromEnd === 0) return 'F';
  if (fromEnd === 1) return 'SF';
  if (fromEnd === 2) return 'QF';
  return `R${roundNumber}`;
}

/**
 * Pusta drabinka do podglądu przed losowaniem — sama struktura rund
 * z nieobsadzonymi miejscami, w rozmiarze wynikającym z liczby zgłoszeń.
 */
export function placeholderBracketRounds(slotCount: number): BracketRound[] {
  if (slotCount < 2) return [];

  const bracketSize = nextPowerOfTwo(slotCount);
  const totalRounds = Math.round(Math.log2(bracketSize));
  const rounds: BracketRound[] = [];

  for (let round = 1; round <= totalRounds; round += 1) {
    const matchesInRound = bracketSize / 2 ** round;
    const name = backendRoundName(round, totalRounds);
    const matches: BracketMatch[] = [];

    for (let position = 1; position <= matchesInRound; position += 1) {
      matches.push({
        id: `preview-${name}-${position}`,
        home: { kind: 'pending' },
        away: { kind: 'pending' },
        status: 'scheduled',
      });
    }

    rounds.push({ id: name, name: bracketRoundLabel(name, matchesInRound), matches });
  }

  return rounds;
}

/**
 * Liczba grup, jaką utworzy backend przy losowaniu GROUP_PLAYOFF: `groupSize`
 * to maksymalny rozmiar grupy, podział jest wyrównany i żadna grupa nie może
 * być singlem (min. 2 sloty).
 */
export function plannedGroupCount(slotCount: number, groupSize: number): number {
  if (slotCount < 2 || groupSize < 2) return 0;
  return Math.max(1, Math.min(Math.ceil(slotCount / groupSize), Math.floor(slotCount / 2)));
}

/** Plan terminarza każdy z każdym: liczba kolejek i meczów dla n slotów. */
export function roundRobinPlan(slotCount: number): { rounds: number; matches: number } {
  if (slotCount < 2) return { rounds: 0, matches: 0 };
  return {
    rounds: slotCount % 2 === 0 ? slotCount - 1 : slotCount,
    matches: (slotCount * (slotCount - 1)) / 2,
  };
}

/**
 * Mapa `userId → seed` obejmująca obie osoby slotu — w deblu rozstawienie
 * przypisane kapitanowi dotyczy całej pary.
 */
export function buildSeedMap(participants: readonly ApiParticipant[]): Map<string, number> {
  const seeds = new Map<string, number>();
  for (const participant of participants) {
    if (participant.seed === null) continue;
    seeds.set(participant.userId, participant.seed);
    if (participant.partnerId) seeds.set(participant.partnerId, participant.seed);
  }
  return seeds;
}

/** Slot drabinki z listy identyfikatorów drużyny — z nazwą pary i rozstawieniem. */
function toSlot(
  ids: readonly string[],
  names: ReadonlyMap<string, string>,
  seeds: ReadonlyMap<string, number>,
): BracketSlot {
  if (ids.length === 0) return { kind: 'pending' };

  const name = ids.map((id) => names.get(id) ?? '…').join(' / ');
  const seed = ids.map((id) => seeds.get(id)).find((value) => value !== undefined);
  return { kind: 'player', player: { id: ids[0] ?? name, name, ...(seed ? { seed } : {}) } };
}

/** Mapuje mecz API na kartę drabinki — z wynikami setów i tie-breakami. */
export function toBracketMatchView(
  match: ApiMatch,
  names: ReadonlyMap<string, string>,
  seeds: ReadonlyMap<string, number>,
): BracketMatch {
  const homeScores = match.setScores.map((set) => set.score1);
  const awayScores = match.setScores.map((set) => set.score2);
  const homeTiebreaks: Record<number, number> = {};
  const awayTiebreaks: Record<number, number> = {};
  match.setScores.forEach((set, index) => {
    if (set.tiebreakScore1 !== null) homeTiebreaks[index] = set.tiebreakScore1;
    if (set.tiebreakScore2 !== null) awayTiebreaks[index] = set.tiebreakScore2;
  });

  const scheduleLabel = shortTime(match.scheduledTime);

  return {
    id: match.id,
    home: toSlot(match.team1Ids, names, seeds),
    away: toSlot(match.team2Ids, names, seeds),
    ...(homeScores.length > 0 ? { homeScores, awayScores } : {}),
    ...(Object.keys(homeTiebreaks).length > 0 ? { homeTiebreaks } : {}),
    ...(Object.keys(awayTiebreaks).length > 0 ? { awayTiebreaks } : {}),
    ...(match.result === 'PLAYER1_WIN'
      ? { winner: 'home' as const }
      : match.result === 'PLAYER2_WIN'
        ? { winner: 'away' as const }
        : {}),
    status: toViewStatus(match.status),
    ...(scheduleLabel ? { scheduleLabel } : {}),
  };
}

/** Drabinka gotowa do wyświetlenia — rundy główne i mecz o 3. miejsce osobno. */
export interface BracketView {
  rounds: BracketRound[];
  /** Mecz o 3. miejsce (runda `3P`) — rysowany poza siatką drabinki. */
  thirdPlace: BracketMatch | null;
}

/**
 * Buduje widok drabinki z odpowiedzi API.
 *
 * Para z wolnym losem nie ma meczu w R1 (backend pomija slot), więc luki
 * w pierwszej rundzie uzupełniamy syntetyczną kartą „zawodnik / Wolny los” —
 * awansującego odczytujemy ze slotu drugiej rundy. Dzięki temu układ i linie
 * łączące (`computeBracketLayout` paruje mecze po indeksie) zawsze się zgadzają.
 */
export function toBracketView(
  bracket: ApiBracket,
  names: ReadonlyMap<string, string>,
  seeds: ReadonlyMap<string, number>,
): BracketView {
  const mainRounds = bracket.rounds
    .filter((round) => round.name !== '3P')
    .slice()
    .sort((a, b) => a.roundNumber - b.roundNumber);

  const rounds: BracketRound[] = mainRounds.map((round, roundIndex) => {
    const matchesInRound = bracket.bracketSize / 2 ** (roundIndex + 1);
    const byPosition = new Map(
      round.matches.map((match) => [match.bracketPosition ?? 0, match] as const),
    );
    const nextRound = mainRounds[roundIndex + 1];
    const nextByPosition = new Map(
      (nextRound?.matches ?? []).map((match) => [match.bracketPosition ?? 0, match] as const),
    );

    const matches: BracketMatch[] = [];
    for (let position = 1; position <= matchesInRound; position += 1) {
      const match = byPosition.get(position);
      if (match) {
        matches.push(toBracketMatchView(match, names, seeds));
        continue;
      }

      // Luka w pierwszej rundzie = wolny los. Awansujący siedzi już w slocie
      // następnej rundy: nieparzysta pozycja zasila team1, parzysta team2.
      const nextMatch = nextByPosition.get(Math.ceil(position / 2));
      const advancingIds =
        position % 2 === 1 ? (nextMatch?.team1Ids ?? []) : (nextMatch?.team2Ids ?? []);

      matches.push({
        id: `bye-${round.name}-${position}`,
        home: toSlot(advancingIds, names, seeds),
        away: { kind: 'bye' },
        status: 'scheduled',
      });
    }

    return {
      id: round.name,
      name: bracketRoundLabel(round.name, matchesInRound),
      matches,
    };
  });

  const thirdPlaceMatch = bracket.rounds
    .find((round) => round.name === '3P')
    ?.matches.find((match) => match.status !== 'CANCELLED');

  return {
    rounds,
    thirdPlace: thirdPlaceMatch ? toBracketMatchView(thirdPlaceMatch, names, seeds) : null,
  };
}

/**
 * Nazwa zwycięzcy turnieju — z rozstrzygniętego finału (ostatniej rundy
 * głównej drabinki) albo `null`, dopóki finał nie ma zwycięzcy.
 */
export function championLabel(view: BracketView): string | null {
  const final = view.rounds[view.rounds.length - 1]?.matches[0];
  if (!final || !final.winner) return null;

  const slot = final.winner === 'home' ? final.home : final.away;
  return slot.kind === 'player' ? slot.player.name : null;
}

/** Wpis podium: miejsce 1-3 i etykieta zawodnika/pary. */
export interface PodiumEntry {
  place: 1 | 2 | 3;
  label: string;
}

/** Nazwa obsadzonego slotu meczu drabinki albo `null`. */
function slotLabelOf(slot: BracketSlot): string | null {
  return slot.kind === 'player' ? slot.player.name : null;
}

/**
 * Podium turnieju (miejsca 1-3) — pojawia się, gdy rozstrzygną się wszystkie
 * decydujące mecze, bez czekania na ręczne zakończenie rozgrywki:
 * <ul>
 *   <li>drabinka — wymaga rozstrzygniętego finału ORAZ meczu o 3. miejsce,
 *       jeśli został wylosowany (do tego czasu zwraca pustą listę); bez meczu
 *       o 3. miejsce przegrani półfinałów dzielą 3-4, więc trójki nie ma
 *       i podium kończy się na miejscach 1-2,</li>
 *   <li>bez drabinki — tabela grupy finałowej / grupy o podium (kubełek 0),
 *       a dla ligi jedyna tabela bez numeru grupy; podium dopiero, gdy każdy
 *       mecz tego kubełka jest rozstrzygnięty (wcześniej tabela jeszcze się
 *       zmienia).</li>
 * </ul>
 * Miejsca rosnąco; nieobsadzone lub niejednoznaczne są pomijane.
 */
export function podiumEntries(
  bracketView: BracketView | null,
  standings: readonly ApiStanding[],
  matches: readonly ApiMatch[],
  names: ReadonlyMap<string, string>,
): PodiumEntry[] {
  if (bracketView) {
    const final = bracketView.rounds[bracketView.rounds.length - 1]?.matches[0];
    if (!final || !final.winner) return [];

    // Wylosowany mecz o 3. miejsce, który jeszcze się nie rozstrzygnął,
    // wstrzymuje całe podium — trójka dopiero po jego zakończeniu.
    const thirdPlace = bracketView.thirdPlace;
    if (thirdPlace && !thirdPlace.winner) return [];

    const entries: PodiumEntry[] = [];
    const first = slotLabelOf(final.winner === 'home' ? final.home : final.away);
    const second = slotLabelOf(final.winner === 'home' ? final.away : final.home);
    if (first) entries.push({ place: 1, label: first });
    if (second) entries.push({ place: 2, label: second });

    if (thirdPlace && thirdPlace.winner) {
      const third = slotLabelOf(thirdPlace.winner === 'home' ? thirdPlace.home : thirdPlace.away);
      if (third) entries.push({ place: 3, label: third });
    }
    return entries;
  }

  const bucket = standings.some((standing) => standing.groupNumber === 0) ? 0 : null;

  // Tabela rozstrzyga podium dopiero w komplecie — dopóki którykolwiek mecz
  // kubełka czeka, miejsca mogą się jeszcze zmienić.
  const bucketMatches = matches.filter(
    (match) =>
      match.bracketPosition === null &&
      (bucket === null ? match.groupNumber === null : match.groupNumber === bucket),
  );
  if (bucketMatches.length === 0 || !bucketMatches.every((match) => isResolved(match.status))) {
    return [];
  }

  return standings
    .filter((standing) =>
      bucket === null ? standing.groupNumber === null : standing.groupNumber === bucket,
    )
    .filter((standing) => standing.rank >= 1 && standing.rank <= 3)
    .sort((a, b) => a.rank - b.rank)
    .map((standing) => ({
      place: standing.rank as 1 | 2 | 3,
      label: [standing.userId, ...(standing.partnerId ? [standing.partnerId] : [])]
        .map((id) => names.get(id) ?? '…')
        .join(' / '),
    }));
}

/* --- Grupy i kolejki --- */

/** Członek grupy — slot z etykietą pary i rozstawieniem. */
export interface GroupMember {
  ids: string[];
  label: string;
  seed: number | null;
}

/** Karta grupy w widoku po losowaniu. */
export interface GroupView {
  /** Numer grupy; `0` oznacza grupę finałową GROUP_PLAYOFF. */
  number: number;
  members: GroupMember[];
  matchesTotal: number;
  matchesResolved: number;
}

/** Statusy meczu uznawane przez backend za rozstrzygnięte (warunek awansu). */
const RESOLVED_STATUSES: ReadonlySet<ApiMatchStatus> = new Set([
  'COMPLETED',
  'WALKOVER',
  'CANCELLED',
]);

/** Czy mecz jest rozstrzygnięty (wynik, walkower albo anulowanie). */
export function isResolved(status: ApiMatchStatus): boolean {
  return RESOLVED_STATUSES.has(status);
}

function memberLabel(ids: readonly string[], names: ReadonlyMap<string, string>): string {
  return ids.map((id) => names.get(id) ?? '…').join(' / ');
}

/**
 * Buduje karty grup fazy grupowej z przydziału `participant.groupNumber`
 * (nadawanego przy losowaniu) i liczników meczów per grupa.
 */
export function groupsView(
  participants: readonly ApiParticipant[],
  matches: readonly ApiMatch[],
  names: ReadonlyMap<string, string>,
): GroupView[] {
  const byGroup = new Map<number, GroupMember[]>();
  for (const participant of participants) {
    if (participant.groupNumber === null || participant.groupNumber < 1) continue;
    const ids = participant.partnerId
      ? [participant.userId, participant.partnerId]
      : [participant.userId];
    const members = byGroup.get(participant.groupNumber) ?? [];
    members.push({ ids, label: memberLabel(ids, names), seed: participant.seed });
    byGroup.set(participant.groupNumber, members);
  }

  return [...byGroup.entries()]
    .sort(([a], [b]) => a - b)
    .map(([number, members]) => {
      const groupMatches = matches.filter((match) => match.groupNumber === number);
      return {
        number,
        members: members.sort((a, b) => a.label.localeCompare(b.label, 'pl-PL')),
        matchesTotal: groupMatches.length,
        matchesResolved: groupMatches.filter((match) => isResolved(match.status)).length,
      };
    });
}

/**
 * Karta grupy finałowej GROUP_PLAYOFF (`secondPhaseType = FINAL_GROUP`).
 * Skład odczytujemy z meczów `groupNumber = 0` — zgłoszenia zachowują numer
 * grupy z fazy grupowej, więc nie da się go wziąć z `participants`.
 */
export function finalGroupView(
  matches: readonly ApiMatch[],
  names: ReadonlyMap<string, string>,
): GroupView | null {
  return groupViewFromMatches(matches, 0, names);
}

/**
 * Karta jednej grupy ze składem odczytanym z meczów o danym numerze.
 * Zwraca `null`, gdy grupa nie ma ani jednego meczu.
 */
function groupViewFromMatches(
  matches: readonly ApiMatch[],
  number: number,
  names: ReadonlyMap<string, string>,
): GroupView | null {
  const groupMatches = matches.filter((match) => match.groupNumber === number);
  if (groupMatches.length === 0) return null;

  const teams = new Map<string, string[]>();
  for (const match of groupMatches) {
    for (const ids of [match.team1Ids, match.team2Ids]) {
      if (ids.length > 0) teams.set(ids.join('+'), [...ids]);
    }
  }

  return {
    number,
    members: [...teams.values()]
      .map((ids) => ({ ids, label: memberLabel(ids, names), seed: null }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pl-PL')),
    matchesTotal: groupMatches.length,
    matchesResolved: groupMatches.filter((match) => isResolved(match.status)).length,
  };
}

/**
 * Mistrz wyłoniony z tabeli — wiersz z miejscem 1 we wskazanym kubełku:
 * `0` dla grupy finałowej / grupy o podium, `null` dla tabeli ligi.
 * Zwraca `null`, dopóki kubełek nie ma wierszy (żaden mecz nierozstrzygnięty).
 */
export function groupChampionLabel(
  standings: readonly ApiStanding[],
  names: ReadonlyMap<string, string>,
  bucket: number | null,
): string | null {
  const winner = standings.find(
    (standing) =>
      (bucket === null ? standing.groupNumber === null : standing.groupNumber === bucket) &&
      standing.rank === 1,
  );
  if (!winner) return null;
  const ids = [winner.userId, ...(winner.partnerId ? [winner.partnerId] : [])];
  return ids.map((id) => names.get(id) ?? '…').join(' / ');
}

/** Grupa klasyfikacyjna drugiej fazy: kubełek meczów i miejsce, o które gra. */
export interface ClassificationGroupView {
  /** Numer kubełka meczów (0 = grupa 1. miejsc, -1 = 2. miejsc, ...). */
  groupNumber: number;
  /** Miejsce z grup fazy 1 tworzące tę grupę (1 = walka o podium). */
  rank: number;
  view: GroupView;
}

/**
 * Grupy klasyfikacyjne drugiej fazy (`secondPhaseType = CLASSIFICATION_GROUPS`)
 * odczytane z meczów: kubełek 0 to grupa 1. miejsc (podium), kubełki ujemne —
 * grupy kolejnych miejsc. Posortowane od podium w dół.
 */
export function classificationGroupsView(
  matches: readonly ApiMatch[],
  names: ReadonlyMap<string, string>,
): ClassificationGroupView[] {
  const buckets = [
    ...new Set(
      matches
        .map((match) => match.groupNumber)
        .filter((number): number is number => number !== null && number <= 0),
    ),
  ].sort((a, b) => b - a);

  return buckets
    .map((number) => {
      const view = groupViewFromMatches(matches, number, names);
      return view ? { groupNumber: number, rank: number === 0 ? 1 : 1 - number, view } : null;
    })
    .filter((group): group is ClassificationGroupView => group !== null);
}

/**
 * Grupy fazy grupowej odczytane z samych meczów — wariant dla ekranu
 * publicznego, który nie ma dostępu do zgłoszeń (a więc i do
 * `participant.groupNumber`, na którym stoi {@link groupsView}).
 */
export function groupsFromMatches(
  matches: readonly ApiMatch[],
  names: ReadonlyMap<string, string>,
): GroupView[] {
  const numbers = [
    ...new Set(
      matches
        .map((match) => match.groupNumber)
        .filter((number): number is number => number !== null && number >= 1),
    ),
  ].sort((a, b) => a - b);

  return numbers
    .map((number) => groupViewFromMatches(matches, number, names))
    .filter((group): group is GroupView => group !== null);
}

/**
 * Skład tabeli SINGLE_GROUP odczytany z meczów (kolejki mają `groupNumber = null`).
 *
 * Nie da się go wziąć ze zgłoszeń: przy zapisach ponad limit losowanie bierze
 * pierwszych `maxPlayers` slotów wg kolejności zapisów, a reszta zostaje na
 * liście rezerwowej — w samym zgłoszeniu nic ich nie odróżnia. Liczenie składu
 * ze zgłoszeń wstawiało do tabeli rezerwowych, którzy nie mają ani jednego
 * meczu. Mecze są jedynym źródłem prawdy o tym, kogo wylosowano.
 */
export function singleGroupRoster(
  matches: readonly ApiMatch[],
  names: ReadonlyMap<string, string>,
): GroupMember[] {
  const teams = new Map<string, string[]>();
  for (const match of matches) {
    if (match.groupNumber !== null) continue;
    for (const ids of [match.team1Ids, match.team2Ids]) {
      if (ids.length > 0) teams.set(ids.join('+'), [...ids]);
    }
  }

  return [...teams.values()]
    .map((ids) => ({ ids, label: memberLabel(ids, names), seed: null }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pl-PL'));
}

/** Wiersz tabeli grupy gotowy do wyświetlenia. */
export interface GroupTableRow {
  ids: string[];
  label: string;
  /** Miejsce w tabeli; `null`, dopóki nie rozegrano żadnego meczu grupy. */
  rank: number | null;
  played: number;
  wins: number;
  losses: number;
  /** Bilans gemów (wygrane − przegrane). */
  gamesBalance: number;
  points: number;
  /** Czy to miejsce awansuje do fazy finałowej. */
  advances: boolean;
  /**
   * Czy drużyna zajmuje w tej chwili jedno z dodatkowych miejsc awansu
   * („awans z dalszych miejsc" — najlepsze bilanse z kolejnego miejsca
   * cross-group). Projekcja na żywo; rozstrzyga stan po ostatnim meczu grup.
   */
  extraQualifier: boolean;
}

/**
 * Kapitanowie drużyn, które przy bieżących tabelach zajmują dodatkowe miejsca
 * awansu („awans z dalszych miejsc"). Odwzorowuje selekcję backendu przy
 * zamykaniu fazy grupowej: z każdej grupy liczy się drużyna z miejsca
 * `advancingPerGroup + 1`, porównywana cross-group bilansem punktów, potem
 * setów, potem gemów (komparator BEST_QUALIFIER_ORDER); remis domyka
 * stabilnie identyfikator kapitana.
 */
export function extraQualifierCaptains(
  standings: readonly ApiStanding[],
  advancingPerGroup: number | null,
  extraQualifiers: number | null,
): Set<string> {
  if (
    advancingPerGroup === null ||
    advancingPerGroup < 1 ||
    extraQualifiers === null ||
    extraQualifiers < 1
  ) {
    return new Set();
  }
  const nextRank = advancingPerGroup + 1;
  const candidates = standings
    .filter(
      (standing) =>
        standing.groupNumber !== null && standing.groupNumber > 0 && standing.rank === nextRank,
    )
    .sort(
      (a, b) =>
        b.points - a.points ||
        b.setsWon - b.setsLost - (a.setsWon - a.setsLost) ||
        b.gamesWon - b.gamesLost - (a.gamesWon - a.gamesLost) ||
        a.userId.localeCompare(b.userId),
    );
  return new Set(candidates.slice(0, extraQualifiers).map((standing) => standing.userId));
}

/**
 * Skleja skład grupy z tabelą wyników w wiersze do wyświetlenia.
 *
 * Zawodnicy bez wpisu w tabeli (przed pierwszym rozstrzygniętym meczem grupy)
 * dostają zera i miejsce `null`. Porządek: rank rosnąco, brak ranku na końcu
 * alfabetycznie. `advancingPerGroup = null` wyłącza oznaczanie awansów
 * (tabela SINGLE_GROUP, grupa finałowa). `extraCaptains` (z
 * {@link extraQualifierCaptains}) oznacza drużyny zajmujące w tej chwili
 * dodatkowe miejsca awansu z dalszych miejsc.
 */
export function buildGroupTable(
  groupNumber: number | null,
  members: readonly GroupMember[],
  standings: readonly ApiStanding[],
  names: ReadonlyMap<string, string>,
  advancingPerGroup: number | null,
  extraCaptains?: ReadonlySet<string>,
): GroupTableRow[] {
  const byCaptain = new Map(
    standings
      .filter((standing) => standing.groupNumber === groupNumber)
      .map((standing) => [standing.userId, standing] as const),
  );

  const rows = members.map((member) => {
    const standing = byCaptain.get(member.ids[0] ?? '');
    return {
      ids: [...member.ids],
      label: member.label || member.ids.map((id) => names.get(id) ?? '…').join(' / '),
      rank: standing ? standing.rank : null,
      played: standing?.matchesPlayed ?? 0,
      wins: standing?.wins ?? 0,
      losses: standing?.losses ?? 0,
      gamesBalance: standing ? standing.gamesWon - standing.gamesLost : 0,
      points: standing?.points ?? 0,
      advances: false,
      extraQualifier: false,
    };
  });

  rows.sort((a, b) => {
    if (a.rank !== null && b.rank !== null) return a.rank - b.rank;
    if (a.rank !== null) return -1;
    if (b.rank !== null) return 1;
    return a.label.localeCompare(b.label, 'pl-PL');
  });

  if (advancingPerGroup !== null && advancingPerGroup > 0) {
    for (const row of rows) {
      row.advances = row.rank !== null && row.rank <= advancingPerGroup;
    }
  }
  if (extraCaptains && extraCaptains.size > 0) {
    for (const row of rows) {
      row.extraQualifier = !row.advances && extraCaptains.has(row.ids[0] ?? '');
    }
  }
  return rows;
}

/** Kolejka gier systemu każdy z każdym. */
export interface RoundRobinRound {
  /** Kod rundy z API, np. `Round 1`. */
  name: string;
  /** Etykieta PL, np. „1. kolejka”. */
  label: string;
  matches: BracketMatch[];
}

/**
 * Grupuje mecze w kolejki („Round N”). Dla SINGLE_GROUP podaj `groupNumber =
 * null`, dla grupy GROUP_PLAYOFF — jej numer, dla grupy finałowej — `0`.
 */
export function roundRobinRounds(
  matches: readonly ApiMatch[],
  groupNumber: number | null,
  names: ReadonlyMap<string, string>,
  seeds: ReadonlyMap<string, number>,
): RoundRobinRound[] {
  const byRound = new Map<number, ApiMatch[]>();
  for (const match of matches) {
    if (match.groupNumber !== groupNumber) continue;
    const parsed = /^Round (\d+)$/.exec(match.round ?? '');
    if (!parsed) continue;
    const number = Number(parsed[1]);
    const list = byRound.get(number) ?? [];
    list.push(match);
    byRound.set(number, list);
  }

  return [...byRound.entries()]
    .sort(([a], [b]) => a - b)
    .map(([number, roundMatches]) => ({
      name: `Round ${number}`,
      label: `${number}. kolejka`,
      matches: roundMatches
        .slice()
        .sort((a, b) => (a.matchNumber ?? 0) - (b.matchNumber ?? 0))
        .map((match) => toBracketMatchView(match, names, seeds)),
    }));
}

/* --- Lista meczów i terminarz --- */

/**
 * Zapis wyniku w setach jednym napisem, np. „6:4, 7:6(5)"; `null` bez setów.
 * Walkower nie ma setów, ale ma rozstrzygnięcie — sygnalizujemy go osobno,
 * żeby kolumna wyniku nie była pusta (etykietę podstawia warstwa widoku).
 */
export function scoreSummary(match: ApiMatch): string | null {
  if (match.status === 'WALKOVER') return null;
  if (match.setScores.length === 0) return null;
  return match.setScores
    .map((set) => {
      const tiebreak =
        set.tiebreakScore1 !== null && set.tiebreakScore2 !== null
          ? `(${Math.min(set.tiebreakScore1, set.tiebreakScore2)})`
          : '';
      return `${set.score1}:${set.score2}${tiebreak}`;
    })
    .join(', ');
}

/** Jeden dzień terminarza — mecze z tą samą zaplanowaną datą. */
export interface ScheduleDay {
  /** Data ISO (yyyy-MM-dd). */
  date: string;
  matches: ApiMatch[];
}

/**
 * Grupuje mecze z zaplanowanym terminem po dniach (rosnąco); w obrębie dnia
 * sortuje po godzinie, a przy tej samej godzinie po kolejce kortu. Mecze bez
 * daty są pomijane.
 */
export function groupMatchesByDay(matches: readonly ApiMatch[]): ScheduleDay[] {
  const byDate = new Map<string, ApiMatch[]>();
  for (const match of matches) {
    if (!match.scheduledDate) continue;
    const list = byDate.get(match.scheduledDate) ?? [];
    list.push(match);
    byDate.set(match.scheduledDate, list);
  }

  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, dayMatches]) => ({
      date,
      matches: dayMatches
        .slice()
        .sort(
          (a, b) =>
            (a.scheduledTime ?? '99:99').localeCompare(b.scheduledTime ?? '99:99') ||
            (a.matchNumber ?? 99) - (b.matchNumber ?? 99),
        ),
    }));
}

/* --- Stan losowania i fazy finałowej --- */

/** Etap fazy finałowej GROUP_PLAYOFF. */
export type FinalPhaseState =
  /** System bez fazy finałowej (PLAYOFF, SINGLE_GROUP…). */
  | 'not-applicable'
  /** Faza grupowa trwa — są nierozstrzygnięte mecze grupowe. */
  | 'waiting'
  /** Wszystkie mecze grupowe rozstrzygnięte — można losować fazę finałową. */
  | 'ready'
  /** Faza finałowa już wylosowana (drabinka albo grupa finałowa). */
  | 'drawn';

/** Czy faza finałowa GROUP_PLAYOFF jest już wylosowana. */
export function finalPhaseDrawn(
  competition: Pick<ApiCompetition, 'bracketSize'>,
  matches: readonly ApiMatch[],
): boolean {
  return competition.bracketSize !== null || matches.some((match) => match.groupNumber === 0);
}

/** Liczba nierozstrzygniętych meczów fazy grupowej (`groupNumber ≥ 1`). */
export function unresolvedGroupMatches(matches: readonly ApiMatch[]): number {
  return matches.filter(
    (match) => match.groupNumber !== null && match.groupNumber > 0 && !isResolved(match.status),
  ).length;
}

/** Wyznacza etap fazy finałowej GROUP_PLAYOFF — patrz {@link FinalPhaseState}. */
export function finalPhaseState(
  competition: Pick<ApiCompetition, 'system' | 'bracketSize'>,
  matches: readonly ApiMatch[],
): FinalPhaseState {
  if (competition.system !== 'GROUP_PLAYOFF') return 'not-applicable';
  if (finalPhaseDrawn(competition, matches)) return 'drawn';

  const groupMatches = matches.filter(
    (match) => match.groupNumber !== null && match.groupNumber > 0,
  );
  if (groupMatches.length === 0) return 'waiting';
  return unresolvedGroupMatches(matches) === 0 ? 'ready' : 'waiting';
}
